// ===== Google Maps Style Venue Gallery =====
// Responsive: Desktop (bento box) + Mobile (swipeable carousel)
// Full-screen lightbox with swipe navigation
// Supports filtering, prioritization, category tabs, and 360° Panellum viewer

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import Panellum360Viewer from './Panellum360Viewer';

// ===== LAMBDA RESIZE CONFIG =====
const CDN_BASE = 'https://media.honestlee.app';

const getResizeUrl = (s3Key, width, height = null, quality = 75) => {
  if (!s3Key) return '';
  let url = `${CDN_BASE}/r?key=${encodeURIComponent(s3Key)}&w=${width}&f=webp&q=${quality}`;
  if (height) url += `&h=${height}&fit=cover`;
  return url;
};

const extractS3Key = (url) => {
  if (!url) return null;
  if (url.startsWith('venue-media/') || url.startsWith('review-images/')) return url;
  const match = url.match(/(?:media\.honestlee\.app|cloudfront\.net|s3[^/]*\.amazonaws\.com)\/(.+?)(?:\?|$)/);
  return match ? match[1] : null;
};

// ===== MEDIA TYPE CONFIGURATION =====
// Types to hide from all galleries (internal/boring)
const HIDDEN_TYPES = [
  'PAYMENT_METHODS',
  'WIFI_PASSWORD',
  'WIFI_SIGN_EXISTING', 
  'WIFI_BOASTING_SPEED',
  'DOC_LICENSE',
  'SELFIE_OWNER_AGENT',
  'SCREENSHOT_GPS_CHANGE',
  'QR_INSTALL_SPOT',
  'RECEIPTS',
  'STAFF_CONTACTS',
  'MANAGER_CONTACTS',
  'POSSYSTEM',
  'ZONE_PHOTO',
  // Policy types
  'POLICY_PAYMENT',
  'POLICY_SMOKING',
  'POLICY_OUTSIDE_FOOD',
  'POLICY_DRESS_CODE',
  'POLICY_AGE_RESTRICTION',
  'POLICY_RESERVATION',
  'POLICY_CANCELLATION',
  'POLICY_REFUND',
  'POLICY_PET',
  'POLICY_ALCOHOL',
  'POLICY_NOISE',
  'POLICY_PHOTOGRAPHY',
  'POLICY_TERMS_CONDITIONS',
  'POLICY_PRIVACY',
  'POLICY_LIABILITY',
];

// Tab definitions - each tab has types that belong to it
// Order matters - tabs will appear in this order
const TAB_CONFIG = [
  {
    id: 'photos',
    label: 'Photos',
    icon: 'photo',
    types: null, // null means "all remaining types not in other tabs"
  },
  {
    id: 'menu',
    label: 'Menu',
    icon: 'menu',
    types: ['MENU_BOARD', 'MENU_PRICES', 'KIDS_MENU', 'COFFEE_MENU'],
  },
  {
    id: '360',
    label: '360°',
    icon: '360',
    types: ['PANO_360'],
    viewer: 'panellum', // Special viewer for this tab
  },
  {
    id: 'panorama',
    label: 'Panorama',
    icon: 'panorama',
    types: ['VIEW_PANORAMA'],
  },
  {
    id: 'food',
    label: 'Food & Drinks',
    icon: 'food',
    types: ['FOOD_DISH', 'FOOD_DISPLAY_COUNTER', 'DRINKS_BAR', 'ICE_CREAM_FREEZERS'],
  },
  {
    id: 'vibe',
    label: 'Vibe',
    icon: 'vibe',
    types: ['VIBE_INTERIOR', 'SEATING_AREA_WORK'],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    icon: 'workspace',
    types: ['CHARGING_PORTS', 'WORKSTATIONS_LAPTOPS', 'FREE_WATER_REFILLS'],
  },
];

// Get all types that belong to specific tabs (for filtering "photos" tab)
const TABBED_TYPES = TAB_CONFIG
  .filter(tab => tab.types !== null)
  .flatMap(tab => tab.types);

// Priority order for main gallery (lower index = shown first)
const PRIORITY_ORDER = [
  'OUTSIDE_VIEW',
  'VIBE_INTERIOR',
  'SEATING_AREA_WORK',
  'VIEW_PANORAMA',
  'FOOD_DISH',
  'DRINKS_BAR',
  'COFFEE_MACHINE',
  'BARISTA_STATION',
  'COUNTER',
  'COUNTER_AREA',
  'LOGO',
  'SIGNBOARD',
  'AMENITIES',
  'CHARGING_PORTS',
  'WORKSTATIONS_LAPTOPS',
  'FOOD_DISPLAY_COUNTER',
  'ICE_CREAM_FREEZERS',
  'FAMILY_KIDS_AREA',
  'HIGH_CHAIRS',
  'PET_AREA',
  'PARKING_AREA',
  'TOILET_FACILITIES',
  'ACCESSIBILITY',
  'USER_GENERAL',
];

// Get priority score (lower = higher priority)
const getPriorityScore = (mediaType) => {
  const index = PRIORITY_ORDER.indexOf(mediaType);
  return index === -1 ? 999 : index;
};

// ===== IMAGE SIZES =====
const SIZES = {
  desktop: {
    hero: { w: 900, h: 600, q: 80 },
    thumb: { w: 400, h: 300, q: 70 },
  },
  tablet: {
    hero: { w: 700, h: 500, q: 75 },
    thumb: { w: 300, h: 225, q: 70 },
  },
  mobile: {
    hero: { w: 600, h: 400, q: 75 },
    thumb: { w: 400, h: 300, q: 70 },
    carousel: { w: 350, h: 260, q: 70 },
  },
  lightbox: { w: 1600, q: 85 },
  lightboxThumb: { w: 100, h: 100, q: 60 },
};

// ===== MAIN GALLERY COMPONENT =====
const GoogleMapsGallery = ({ media = [], venueName = 'Venue', speedBadge = null }) => {
  const { t } = useTranslation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [panellumOpen, setPanellumOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('photos');
  const [lightboxSource, setLightboxSource] = useState('photos');
  const carouselRef = useRef(null);

  // Check screen size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Process and categorize images into tabs
  const { tabData, availableTabs } = useMemo(() => {
    const processImage = (item, idx) => {
      const s3Key = item.s3Key || extractS3Key(item.fileUrl) || extractS3Key(item.thumbnailUrl);
      const size = isMobile ? SIZES.mobile : SIZES.desktop;
      
      return {
        id: item._id || item.id || `img-${idx}`,
        mediaType: item.mediaType,
        s3Key,
        hero: s3Key ? getResizeUrl(s3Key, size.hero.w, size.hero.h, size.hero.q) : item.fileUrl,
        thumb: s3Key ? getResizeUrl(s3Key, size.thumb.w, size.thumb.h, size.thumb.q) : item.thumbnailUrl,
        carousel: s3Key ? getResizeUrl(s3Key, SIZES.mobile.carousel.w, SIZES.mobile.carousel.h, SIZES.mobile.carousel.q) : item.thumbnailUrl,
        full: s3Key ? getResizeUrl(s3Key, SIZES.lightbox.w, null, SIZES.lightbox.q) : item.fileUrl,
        lightboxThumb: s3Key ? getResizeUrl(s3Key, SIZES.lightboxThumb.w, SIZES.lightboxThumb.h, SIZES.lightboxThumb.q) : item.thumbnailUrl,
        fallback: item.fileUrl || item.thumbnailUrl,
      };
    };

    // Filter out videos and hidden types
    const validMedia = media.filter(item => 
      !item.isVideo && !HIDDEN_TYPES.includes(item.mediaType)
    );
    
    // Build tab data
    const data = {};
    
    TAB_CONFIG.forEach(tab => {
      if (tab.types === null) {
        // "Photos" tab - all types NOT in other tabs
        data[tab.id] = validMedia
          .filter(item => !TABBED_TYPES.includes(item.mediaType))
          .sort((a, b) => getPriorityScore(a.mediaType) - getPriorityScore(b.mediaType))
          .map(processImage)
          .filter(img => img.s3Key || img.fallback);
      } else {
        // Specific category tab
        data[tab.id] = validMedia
          .filter(item => tab.types.includes(item.mediaType))
          .map(processImage)
          .filter(img => img.s3Key || img.fallback);
      }
    });
    
    // Filter to only tabs with images
    const available = TAB_CONFIG.filter(tab => data[tab.id]?.length > 0);
    
    return { tabData: data, availableTabs: available };
  }, [media, isMobile]);

  // Auto-switch to first available tab if current tab is empty
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(t => t.id === activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  // Get current images based on active tab
  const images = tabData[activeTab] || [];

  // Get tab label for display
  const activeTabConfig = TAB_CONFIG.find(t => t.id === activeTab);
  const activeTabLabel = activeTabConfig?.label || 'Photos';

  // Handle image load
  const handleImageLoad = useCallback((id) => {
    setLoadedImages(prev => ({ ...prev, [id]: true }));
  }, []);

  // Handle image error
  const handleImageError = useCallback((e, fallback) => {
    if (fallback && e.target.src !== fallback) {
      e.target.src = fallback;
    }
  }, []);

  // Check if a tab uses Panellum viewer
  const isPanellumTab = useCallback((tabId) => {
    const tabConfig = TAB_CONFIG.find(t => t.id === tabId);
    return tabConfig?.viewer === 'panellum';
  }, []);

  // Open lightbox or Panellum viewer
  const openLightbox = useCallback((index, source = 'photos') => {
    setCurrentIndex(index);
    setLightboxSource(source);
    
    if (isPanellumTab(source)) {
      setPanellumOpen(true);
    } else {
      setLightboxOpen(true);
    }
    document.body.style.overflow = 'hidden';
  }, [isPanellumTab]);

  // Close lightbox
  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  }, []);

  // Close Panellum viewer
  const closePanellum = useCallback(() => {
    setPanellumOpen(false);
    document.body.style.overflow = '';
  }, []);

  // Get lightbox images based on source
  const lightboxImages = tabData[lightboxSource] || [];

  // Check if we have any content
  const totalImages = Object.values(tabData).reduce((sum, arr) => sum + arr.length, 0);

  if (totalImages === 0) {
    return (
      <div style={styles.emptyState}>
        <PhotoIcon style={{ width: 48, height: 48, color: '#9ca3af' }} />
        <p style={{ color: '#6b7280', marginTop: 12 }}>{t('venue.noPhotos')}</p>
      </div>
    );
  }

  const displayCount = isMobile ? images.length : Math.min(4, images.length);
  const remainingCount = images.length - displayCount;

  // Tab icon renderer
  const renderTabIcon = (iconType) => {
    switch (iconType) {
      case 'photo':
        return <PhotoIcon style={{ width: 16, height: 16 }} />;
      case 'menu':
        return (
          <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        );
      case '360':
        return (
          <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
        );
      case 'panorama':
        return (
          <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'food':
        return (
          <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        );
      case 'vibe':
        return (
          <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        );
      case 'workspace':
        return (
          <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      default:
        return <PhotoIcon style={{ width: 16, height: 16 }} />;
    }
  };

  return (
    <>
      {/* ===== TAB SWITCHER ===== */}
      {availableTabs.length > 1 && (
        <div style={styles.tabContainer}>
          {availableTabs.map(tab => (
            <button
              key={tab.id}
              style={{
                ...styles.tabButton,
                ...(activeTab === tab.id ? styles.tabButtonActive : {}),
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {renderTabIcon(tab.icon)}
              <span>{tab.label} ({tabData[tab.id]?.length || 0})</span>
            </button>
          ))}
        </div>
      )}

      {/* Show message if current tab is empty */}
      {images.length === 0 && (
        <div style={styles.emptyState}>
          <PhotoIcon style={{ width: 48, height: 48, color: '#9ca3af' }} />
          <p style={{ color: '#6b7280', marginTop: 12 }}>
            {t('venue.noPhotos')}
          </p>
        </div>
      )}

      {images.length > 0 && (
        <>
      {/* ===== DESKTOP: Bento Box Layout ===== */}
      {!isMobile && (
        <div style={styles.desktopGrid}>
          {/* Hero Image */}
          <div 
            style={styles.heroContainer}
            onClick={() => openLightbox(0, activeTab)}
          >
            <img
              src={images[0]?.hero}
              alt={venueName}
              style={{
                ...styles.image,
                opacity: loadedImages[images[0]?.id] ? 1 : 0,
              }}
              onLoad={() => handleImageLoad(images[0]?.id)}
              onError={(e) => handleImageError(e, images[0]?.fallback)}
            />
            {!loadedImages[images[0]?.id] && <div style={styles.skeleton} />}
            
            {/* 360° indicator for panoramic tab */}
            {isPanellumTab(activeTab) && (
              <>
                {/* Dark overlay to soften the distorted equirectangular look */}
                <div style={styles.overlay360} />
                <div style={styles.badge360}>
                  <svg viewBox="0 0 24 24" style={{ width: 32, height: 32, fill: 'white' }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                  </svg>
                  <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1 }}>360°</span>
                  <span style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>{t('venue.tapToExploreSphere')}</span>
                </div>
              </>
            )}
            
            {/* Speed Badge (passed from parent) */}
            {speedBadge}
            
            {/* Photo count badge */}
            <div style={styles.photoBadge}>
              <PhotoIcon style={{ width: 16, height: 16 }} />
              <span>{images.length} {activeTabLabel.toLowerCase()}</span>
            </div>
          </div>

          {/* Thumbnail Stack */}
          <div style={styles.thumbStack}>
            {images.slice(1, 4).map((img, idx) => (
              <div 
                key={img.id}
                style={styles.thumbContainer}
                onClick={() => openLightbox(idx + 1, activeTab)}
              >
                <img
                  src={img.thumb}
                  alt={`${venueName} ${idx + 2}`}
                  style={{
                    ...styles.image,
                    opacity: loadedImages[img.id] ? 1 : 0,
                  }}
                  onLoad={() => handleImageLoad(img.id)}
                  onError={(e) => handleImageError(e, img.fallback)}
                />
                {!loadedImages[img.id] && <div style={styles.skeleton} />}
                
                {/* 360° badge on thumbnails */}
                {isPanellumTab(activeTab) && (
                  <>
                    <div style={styles.thumbOverlay360} />
                    <div style={styles.thumbBadge360}>
                      <svg viewBox="0 0 24 24" style={{ width: 10, height: 10, fill: 'white', flexShrink: 0 }}>
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                      </svg>
                      360°
                    </div>
                  </>
                )}
                
                {/* +N overlay on last thumb */}
                {idx === 2 && remainingCount > 0 && (
                  <div style={styles.moreOverlay}>
                    <span style={styles.moreText}>+{remainingCount}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== MOBILE: Swipeable Carousel ===== */}
      {isMobile && (
        <div style={styles.mobileContainer}>
          {/* Speed Badge for mobile */}
          {speedBadge}
          
          <div 
            ref={carouselRef}
            style={styles.carousel}
          >
            {images.map((img, idx) => (
              <div 
                key={img.id}
                style={styles.carouselItem}
                onClick={() => openLightbox(idx, activeTab)}
              >
                <img
                  src={img.carousel}
                  alt={`${venueName} ${idx + 1}`}
                  style={{
                    ...styles.image,
                    opacity: loadedImages[img.id] ? 1 : 0,
                  }}
                  onLoad={() => handleImageLoad(img.id)}
                  onError={(e) => handleImageError(e, img.fallback)}
                />
                {!loadedImages[img.id] && <div style={styles.skeleton} />}
              </div>
            ))}
          </div>
          
          {/* Photo count & scroll hint */}
          <div style={styles.mobileFooter}>
            <div style={styles.photoBadgeMobile}>
              <PhotoIcon style={{ width: 14, height: 14 }} />
              <span>{images.length} {activeTabLabel.toLowerCase()}</span>
            </div>
            <span style={styles.scrollHint}>{t('venue.swipeToSeeMore')}</span>
          </div>
        </div>
      )}
      </>
      )}

      {/* ===== LIGHTBOX (for regular images) ===== */}
      {lightboxOpen && createPortal(
        <Lightbox
          images={lightboxImages}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          onClose={closeLightbox}
          venueName={venueName}
          tabLabel={lightboxSource !== 'photos' ? (TAB_CONFIG.find(t => t.id === lightboxSource)?.label || '') : ''}
        />,
        document.body
      )}

      {/* ===== PANELLUM 360° VIEWER ===== */}
      {panellumOpen && (
        <Panellum360Viewer
          images={lightboxImages}
          initialIndex={currentIndex}
          onClose={closePanellum}
          venueName={venueName}
        />
      )}
    </>
  );
};

// ===== LIGHTBOX COMPONENT =====
const Lightbox = ({ images, currentIndex, setCurrentIndex, onClose, venueName, tabLabel = '' }) => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const thumbnailsRef = useRef(null);

  // Minimum swipe distance
  const minSwipeDistance = 50;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCurrentIndex(i => Math.min(i + 1, images.length - 1));
      if (e.key === 'ArrowLeft') setCurrentIndex(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [images.length, onClose, setCurrentIndex]);

  // Scroll thumbnail into view
  useEffect(() => {
    if (thumbnailsRef.current) {
      const thumb = thumbnailsRef.current.children[currentIndex];
      if (thumb) {
        thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [currentIndex]);

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentIndex < images.length - 1) {
      setCurrentIndex(i => i + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(i => i - 1);
    }
  };

  const handleImageLoad = (id) => {
    setLoadedImages(prev => ({ ...prev, [id]: true }));
  };

  const currentImage = images[currentIndex];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={styles.lightboxOverlay}
      >
        {/* Header */}
        <div style={styles.lightboxHeader}>
          <div style={styles.lightboxCounter}>
            {currentIndex + 1} / {images.length}
          </div>
          <div style={styles.lightboxTitle}>
            {tabLabel ? `${venueName} - ${tabLabel}` : venueName}
          </div>
          <button onClick={onClose} style={styles.closeButton}>
            <XMarkIcon style={{ width: 24, height: 24 }} />
          </button>
        </div>

        {/* Main Image Area */}
        <div 
          style={styles.lightboxMain}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Previous Button */}
          {currentIndex > 0 && (
            <button
              onClick={() => setCurrentIndex(i => i - 1)}
              style={{ ...styles.navButton, left: 16 }}
            >
              <ChevronLeftIcon style={{ width: 32, height: 32 }} />
            </button>
          )}

          {/* Image */}
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            style={styles.lightboxImageContainer}
          >
            {!loadedImages[currentImage?.id] && (
              <div style={styles.lightboxLoader}>
                <div style={styles.spinner} />
              </div>
            )}
            <img
              src={currentImage?.full}
              alt={`${venueName} ${currentIndex + 1}`}
              style={{
                ...styles.lightboxImage,
                opacity: loadedImages[currentImage?.id] ? 1 : 0,
              }}
              onLoad={() => handleImageLoad(currentImage?.id)}
            />
          </motion.div>

          {/* Next Button */}
          {currentIndex < images.length - 1 && (
            <button
              onClick={() => setCurrentIndex(i => i + 1)}
              style={{ ...styles.navButton, right: 16 }}
            >
              <ChevronRightIcon style={{ width: 32, height: 32 }} />
            </button>
          )}
        </div>

        {/* Thumbnail Strip */}
        <div style={styles.thumbnailStrip}>
          <div ref={thumbnailsRef} style={styles.thumbnailScroll}>
            {images.map((img, idx) => (
              <button
                key={img.id}
                onClick={() => setCurrentIndex(idx)}
                style={{
                  ...styles.thumbnailButton,
                  ...(idx === currentIndex ? styles.thumbnailActive : {}),
                }}
              >
                <img
                  src={img.lightboxThumb}
                  alt={`Thumbnail ${idx + 1}`}
                  style={styles.thumbnailImage}
                />
              </button>
            ))}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// ===== STYLES =====
const styles = {
  // Empty state
  emptyState: {
    backgroundColor: '#f3f4f6',
    borderRadius: 16,
    padding: 40,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  // Tab Container
  tabContainer: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
    position: 'relative',
    zIndex: 20,
    overflowX: 'auto',
    paddingBottom: 4,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch',
  },

  tabButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 20,
    border: '1px solid #e5e7eb',
    backgroundColor: '#fff',
    color: '#6b7280',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },

  tabButtonActive: {
    backgroundColor: '#111827',
    borderColor: '#111827',
    color: '#fff',
  },

  // Desktop Grid (Bento Box)
  desktopGrid: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: 0,
    borderRadius: 16,
    overflow: 'hidden',
    height: 400,
    backgroundColor: '#000',
  },

  heroContainer: {
    position: 'relative',
    cursor: 'pointer',
    overflow: 'hidden',
  },

  thumbStack: {
    display: 'grid',
    gridTemplateRows: 'repeat(3, 1fr)',
    gap: 0,
  },

  thumbContainer: {
    position: 'relative',
    cursor: 'pointer',
    overflow: 'hidden',
    borderLeft: '2px solid #000',
  },

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease, opacity 0.3s ease',
  },

  skeleton: {
    position: 'absolute',
    inset: 0,
    backgroundColor: '#e5e7eb',
    animation: 'pulse 1.5s infinite',
  },

  photoBadge: {
    position: 'absolute',
    bottom: 12,
    left: 12,
    backgroundColor: 'rgba(0,0,0,0.7)',
    backdropFilter: 'blur(8px)',
    color: 'white',
    padding: '8px 14px',
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    fontWeight: 500,
  },

  overlay360: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    pointerEvents: 'none',
  },

  badge360: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'rgba(37, 99, 235, 0.92)',
    backdropFilter: 'blur(10px)',
    color: 'white',
    padding: '18px 28px',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    fontSize: 18,
    fontWeight: 700,
    pointerEvents: 'none',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.2)',
  },

  thumbOverlay360: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(37, 99, 235, 0.25)',
    pointerEvents: 'none',
  },

  thumbBadge360: {
    position: 'absolute',
    bottom: 6,
    right: 6,
    backgroundColor: 'rgba(37, 99, 235, 0.95)',
    color: 'white',
    padding: '3px 7px',
    borderRadius: 6,
    fontSize: 9,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  },

  moreOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },

  moreText: {
    color: 'white',
    fontSize: 28,
    fontWeight: 700,
  },

  // Mobile Carousel
  mobileContainer: {
    position: 'relative',
  },

  carousel: {
    display: 'flex',
    gap: 0,
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    borderRadius: 16,
    overflow: 'hidden',
  },

  carouselItem: {
    flexShrink: 0,
    width: '85%',
    height: 250,
    scrollSnapAlign: 'center',
    position: 'relative',
    cursor: 'pointer',
    marginRight: 2,
  },

  mobileFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 4px',
  },

  photoBadgeMobile: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: '#6b7280',
  },

  scrollHint: {
    fontSize: 12,
    color: '#9ca3af',
  },

  // Lightbox
  lightboxOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: '#000',
    zIndex: 99999,
    display: 'flex',
    flexDirection: 'column',
  },

  lightboxHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: 'rgba(0,0,0,0.8)',
  },

  lightboxCounter: {
    color: 'white',
    fontSize: 14,
    fontWeight: 500,
    minWidth: 60,
  },

  lightboxTitle: {
    color: 'white',
    fontSize: 16,
    fontWeight: 600,
    flex: 1,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '0 16px',
  },

  closeButton: {
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: 8,
    padding: 8,
    cursor: 'pointer',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
  },

  lightboxMain: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    touchAction: 'pan-y',
  },

  lightboxImageContainer: {
    maxWidth: '100%',
    maxHeight: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },

  lightboxImage: {
    maxWidth: '100%',
    maxHeight: 'calc(100vh - 180px)',
    objectFit: 'contain',
    borderRadius: 4,
    transition: 'opacity 0.3s ease',
  },

  lightboxLoader: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(255,255,255,0.2)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },

  navButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: 12,
    padding: 12,
    cursor: 'pointer',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
    zIndex: 10,
  },

  thumbnailStrip: {
    backgroundColor: 'rgba(0,0,0,0.9)',
    padding: '12px 0',
  },

  thumbnailScroll: {
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    padding: '0 16px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch',
  },

  thumbnailButton: {
    flexShrink: 0,
    width: 64,
    height: 64,
    borderRadius: 8,
    overflow: 'hidden',
    border: '2px solid transparent',
    padding: 0,
    cursor: 'pointer',
    opacity: 0.5,
    transition: 'all 0.2s ease',
    background: 'none',
  },

  thumbnailActive: {
    border: '2px solid white',
    opacity: 1,
    transform: 'scale(1.05)',
  },

  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
};

// Add CSS animations
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .google-gallery-carousel::-webkit-scrollbar {
    display: none;
  }
`;
if (typeof document !== 'undefined' && !document.querySelector('#google-gallery-styles')) {
  styleSheet.id = 'google-gallery-styles';
  document.head.appendChild(styleSheet);
}

export default GoogleMapsGallery;
